export type ProductIdentifierPair = {
  id?: number | null
  platform_setting?: number
  platform1?: number
  platform2?: number
  platform_identifier1?: string
  platform_identifier2?: string
  platform_product_identifier1?: string | null
  platform_product_identifier2?: string | null
}

/** Backend sends a real id for a DB-saved pair and `null` for an unsaved default. */
export const isPersistedProductIdentifierPairId = (id: unknown): boolean => {
  if (id == null || id === '') return false
  const numericId = Number(id)
  return Number.isInteger(numericId) && numericId > 0
}

export const getPersistedProductIdentifierPairId = (id: unknown): number | undefined => {
  if (!isPersistedProductIdentifierPairId(id)) return undefined
  return Number(id)
}

export const shouldLockIdentifierPair = (
  pair?: Pick<ProductIdentifierPair, 'id'> | null
): boolean => isPersistedProductIdentifierPairId(pair?.id)

export type IdentifierRowPersistState = {
  savedPairId?: number
  selectedIdentifierLocked: boolean
  relatedIdentifierLocked: boolean
}

/** Lock/write only a real backend id (unsaved defaults stay editable). */
export const resolveIdentifierRowPersistState = (
  pair?: Pick<ProductIdentifierPair, 'id'> | null,
  _liveLock?: {
    selectedIdentifier?: string
    relatedIdentifier?: string
    resolvedFromSecondaryStore?: boolean
  }
): IdentifierRowPersistState => {
  const locked = shouldLockIdentifierPair(pair)
  return {
    savedPairId: getPersistedProductIdentifierPairId(pair?.id),
    selectedIdentifierLocked: locked,
    relatedIdentifierLocked: locked,
  }
}

export const attachIdentifierPairIdForSave = (
  pair: ProductIdentifierPair,
  candidateId: unknown
): ProductIdentifierPair => {
  const persistedId = getPersistedProductIdentifierPairId(candidateId)
  if (persistedId == null) return pair
  return {...pair, id: persistedId}
}

export const mergeSavedIdentifierRowPersistState = (
  row: {
    savedPairId?: number
    selectedIdentifierLocked?: boolean
    relatedIdentifierLocked?: boolean
  },
  savedPair?: Pick<ProductIdentifierPair, 'id'> | null
): IdentifierRowPersistState => {
  const persisted = resolveIdentifierRowPersistState(savedPair)
  return {
    savedPairId: persisted.savedPairId ?? row.savedPairId,
    selectedIdentifierLocked:
      persisted.savedPairId != null
        ? persisted.selectedIdentifierLocked
        : Boolean(row.selectedIdentifierLocked),
    relatedIdentifierLocked:
      persisted.savedPairId != null
        ? persisted.relatedIdentifierLocked
        : Boolean(row.relatedIdentifierLocked),
  }
}

export type ResolvedIdentifierPair = {
  savedPair?: ProductIdentifierPair
  selectedIdentifier: string
  relatedIdentifier: string
}

export type SecondaryPlatformIdentifierData = {
  settings: Record<string, any>
  pairs: ProductIdentifierPair[]
}

const normalizeRole = (role?: string | null): string => `${role ?? ''}`.trim().toUpperCase()

const normalizeIdentifierValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return `${value}`.trim()
}

export const pairMatchesPlatformIds = (
  pair: ProductIdentifierPair,
  platformIdA: number,
  platformIdB: number
): boolean => {
  const platform1 = Number(pair.platform1)
  const platform2 = Number(pair.platform2)

  return (
    (platform1 === platformIdA && platform2 === platformIdB) ||
    (platform1 === platformIdB && platform2 === platformIdA)
  )
}

const rolesMatchForPairOrientation = (
  pair: ProductIdentifierPair,
  selectedPlatformId: number,
  relatedPlatformId: number,
  selectedRole: string,
  relatedRole: string
): boolean => {
  const normalizedSelectedRole = normalizeRole(selectedRole)
  const normalizedRelatedRole = normalizeRole(relatedRole)

  if (
    Number(pair.platform1) === selectedPlatformId &&
    Number(pair.platform2) === relatedPlatformId
  ) {
    return (
      normalizeRole(pair.platform_identifier1) === normalizedSelectedRole &&
      normalizeRole(pair.platform_identifier2) === normalizedRelatedRole
    )
  }

  if (
    Number(pair.platform1) === relatedPlatformId &&
    Number(pair.platform2) === selectedPlatformId
  ) {
    return (
      normalizeRole(pair.platform_identifier1) === normalizedRelatedRole &&
      normalizeRole(pair.platform_identifier2) === normalizedSelectedRole
    )
  }

  return false
}

/** Finds a persisted pair for two platforms regardless of platform1/platform2 order. */
export const findSavedProductIdentifierPair = (
  pairs: ProductIdentifierPair[],
  selectedPlatformId: number,
  relatedPlatformId: number,
  selectedRole: string,
  relatedRole?: string
): ProductIdentifierPair | undefined => {
  if (relatedRole) {
    const exactMatch = pairs.find(
      (pair) =>
        pairMatchesPlatformIds(pair, selectedPlatformId, relatedPlatformId) &&
        rolesMatchForPairOrientation(
          pair,
          selectedPlatformId,
          relatedPlatformId,
          selectedRole,
          relatedRole
        )
    )
    if (exactMatch) return exactMatch
  }

  const normalizedSelectedRole = normalizeRole(selectedRole)

  return pairs.find((pair) => {
    if (!pairMatchesPlatformIds(pair, selectedPlatformId, relatedPlatformId)) {
      return false
    }

    if (Number(pair.platform1) === selectedPlatformId) {
      return normalizeRole(pair.platform_identifier1) === normalizedSelectedRole
    }

    if (Number(pair.platform2) === selectedPlatformId) {
      return normalizeRole(pair.platform_identifier2) === normalizedSelectedRole
    }

    return false
  })
}

const getIdentifiersFromSavedPair = (
  pair: ProductIdentifierPair,
  selectedPlatformId: number,
  relatedPlatformId: number
): {selectedIdentifier: string; relatedIdentifier: string} => {
  if (Number(pair.platform1) === selectedPlatformId && Number(pair.platform2) === relatedPlatformId) {
    return {
      selectedIdentifier: normalizeIdentifierValue(pair.platform_product_identifier1),
      relatedIdentifier: normalizeIdentifierValue(pair.platform_product_identifier2),
    }
  }

  if (Number(pair.platform1) === relatedPlatformId && Number(pair.platform2) === selectedPlatformId) {
    return {
      selectedIdentifier: normalizeIdentifierValue(pair.platform_product_identifier2),
      relatedIdentifier: normalizeIdentifierValue(pair.platform_product_identifier1),
    }
  }

  return {selectedIdentifier: '', relatedIdentifier: ''}
}

const roleIdentifierFieldMap: Record<
  string,
  {secondary: string[]; role: string[]; primary?: string[]; rolePrimary?: string[]}
> = {
  PRIMARY: {
    secondary: ['secondary_product_identifier'],
    role: ['main_product_identifier'],
  },
  TICKET: {
    secondary: ['secondary_product_identifier'],
    role: ['ticket_product_identifier', 'ticket_identifier'],
    primary: ['primary_ticket_identifier'],
    rolePrimary: ['ticket_product_identifier', 'ticket_identifier', 'ticket_identifier_primary'],
  },
  ACCOUNTING: {
    secondary: ['secondary_accounting_identifier'],
    role: ['accounting_identifier'],
    primary: ['primary_accounting_identifier'],
    rolePrimary: ['accounting_identifier_primary'],
  },
  SHIPPING: {
    secondary: ['secondary_shipping_identifier'],
    role: ['shipping_identifier'],
    primary: ['primary_shipping_identifier'],
    rolePrimary: ['shipping_identifier_primary'],
  },
  ERP: {
    secondary: ['secondary_erp_identifier'],
    role: ['erp_identifier'],
    primary: ['primary_erp_identifier'],
    rolePrimary: ['erp_identifier_primary'],
  },
  OMS: {
    secondary: ['secondary_oms_identifier', 'secondary_oms_product_identifier'],
    role: ['oms_identifier', 'oms_product_identifier'],
    primary: ['primary_oms_identifier', 'primary_oms_product_identifier'],
    rolePrimary: ['oms_identifier_primary', 'oms_product_identifier_primary'],
  },
}

const getIdentifierFieldKeys = (role: string, pairedRole: string): string[] => {
  const normalizedRole = normalizeRole(role)
  const normalizedPairedRole = normalizeRole(pairedRole)

  if (normalizedRole === 'SECONDARY') {
    return roleIdentifierFieldMap[normalizedPairedRole]?.secondary ?? []
  }

  if (normalizedRole === 'PRIMARY') {
    return (
      roleIdentifierFieldMap[normalizedPairedRole]?.primary ?? roleIdentifierFieldMap.PRIMARY.role
    )
  }

  if (normalizedPairedRole === 'PRIMARY') {
    return (
      roleIdentifierFieldMap[normalizedRole]?.rolePrimary ??
      roleIdentifierFieldMap[normalizedRole]?.role ??
      []
    )
  }

  return roleIdentifierFieldMap[normalizedRole]?.role ?? []
}

const getFirstIdentifierValue = (settings: Record<string, any>, keys: string[]): string => {
  for (const key of keys) {
    const value = normalizeIdentifierValue(settings?.[key])
    if (value) return value
  }
  return ''
}

const getFallbackIdentifierValue = (
  settings: Record<string, any>,
  currentRole: string,
  pairedRole: string
): string => {
  const fieldMap = getIdentifierFieldKeys(currentRole, pairedRole)

  return fieldMap.length ? getFirstIdentifierValue(settings, fieldMap) : ''
}

export const resolveIdentifierPair = (
  settings: Record<string, any>,
  pairs: ProductIdentifierPair[],
  selectedPlatformId: number,
  relatedPlatformId: number,
  selectedRole: string,
  relatedRole: string,
  allowRootFallback = true
): ResolvedIdentifierPair => {
  const savedPair = findSavedProductIdentifierPair(
    pairs,
    selectedPlatformId,
    relatedPlatformId,
    selectedRole,
    relatedRole
  )
  const selectedFallback = allowRootFallback
    ? getFallbackIdentifierValue(settings, selectedRole, relatedRole)
    : ''
  const relatedFallback = allowRootFallback
    ? getFallbackIdentifierValue(settings, relatedRole, selectedRole)
    : ''

  const {selectedIdentifier: savedSelectedIdentifier, relatedIdentifier: savedRelatedIdentifier} =
    savedPair
      ? getIdentifiersFromSavedPair(savedPair, selectedPlatformId, relatedPlatformId)
      : {selectedIdentifier: '', relatedIdentifier: ''}
  const hasCompleteSavedPair = Boolean(savedSelectedIdentifier && savedRelatedIdentifier)
  const hasCompleteFallbackPair = Boolean(selectedFallback && relatedFallback)

  const selectedIdentifier = hasCompleteSavedPair
    ? savedSelectedIdentifier
    : hasCompleteFallbackPair
    ? selectedFallback
    : ''
  const relatedIdentifier = hasCompleteSavedPair
    ? savedRelatedIdentifier
    : hasCompleteFallbackPair
    ? relatedFallback
    : ''

  return {
    savedPair,
    selectedIdentifier,
    relatedIdentifier,
  }
}

/** Identifier pairs for secondary marketplaces are persisted on the secondary platform. */
export const shouldResolveIdentifierFromSecondaryStore = (
  selectedRole: string,
  relatedRole: string
): boolean =>
  normalizeRole(selectedRole) === 'OMS' && normalizeRole(relatedRole) === 'SECONDARY'

export const findSecondaryStoredIdentifierPair = (
  pairs: ProductIdentifierPair[],
  omsPlatformId: number,
  secondaryPlatformId: number
): ProductIdentifierPair | undefined =>
  pairs.find(
    (pair) =>
      Number(pair.platform1) === secondaryPlatformId &&
      Number(pair.platform2) === omsPlatformId &&
      normalizeRole(pair.platform_identifier1) === 'SECONDARY' &&
      normalizeRole(pair.platform_identifier2) === 'OMS'
  )

export const resolveIdentifierPairFromSecondaryStore = (
  secondarySettings: Record<string, any>,
  secondaryPairs: ProductIdentifierPair[],
  omsPlatformId: number,
  secondaryPlatformId: number,
  allowRootFallback = true
): ResolvedIdentifierPair & {resolvedFromSecondaryStore: boolean} => {
  const storedPair = findSecondaryStoredIdentifierPair(
    secondaryPairs,
    omsPlatformId,
    secondaryPlatformId
  )

  const savedOmsIdentifier = normalizeIdentifierValue(storedPair?.platform_product_identifier2)
  const savedSecondaryIdentifier = normalizeIdentifierValue(
    storedPair?.platform_product_identifier1
  )
  const hasCompleteSavedPair = Boolean(savedOmsIdentifier && savedSecondaryIdentifier)

  const selectedFallback =
    allowRootFallback && !hasCompleteSavedPair
      ? getFallbackIdentifierValue(secondarySettings, 'OMS', 'SECONDARY')
      : ''
  const relatedFallback =
    allowRootFallback && !hasCompleteSavedPair
      ? getFallbackIdentifierValue(secondarySettings, 'SECONDARY', 'OMS')
      : ''
  const hasCompleteFallbackPair = Boolean(selectedFallback && relatedFallback)

  const selectedIdentifier = hasCompleteSavedPair
    ? savedOmsIdentifier
    : hasCompleteFallbackPair
    ? selectedFallback
    : ''
  const relatedIdentifier = hasCompleteSavedPair
    ? savedSecondaryIdentifier
    : hasCompleteFallbackPair
    ? relatedFallback
    : ''

  const savedPair =
    storedPair && hasCompleteSavedPair
      ? {
          ...storedPair,
          platform1: omsPlatformId,
          platform2: secondaryPlatformId,
          platform_identifier1: 'OMS',
          platform_identifier2: 'SECONDARY',
          platform_product_identifier1: selectedIdentifier,
          platform_product_identifier2: relatedIdentifier,
        }
      : undefined

  return {
    savedPair,
    selectedIdentifier,
    relatedIdentifier,
    resolvedFromSecondaryStore: Boolean(selectedIdentifier && relatedIdentifier),
  }
}

export const resolveIdentifierPairForRow = (params: {
  settings: Record<string, any>
  pairs: ProductIdentifierPair[]
  selectedPlatformId: number
  relatedPlatformId: number
  selectedRole: string
  relatedRole: string
  allowRootFallback?: boolean
  secondaryStoreData?: SecondaryPlatformIdentifierData
}): ResolvedIdentifierPair & {resolvedFromSecondaryStore: boolean} => {
  const {
    settings,
    pairs,
    selectedPlatformId,
    relatedPlatformId,
    selectedRole,
    relatedRole,
    allowRootFallback = true,
    secondaryStoreData,
  } = params

  const direct = resolveIdentifierPair(
    settings,
    pairs,
    selectedPlatformId,
    relatedPlatformId,
    selectedRole,
    relatedRole,
    allowRootFallback
  )

  if (direct.selectedIdentifier && direct.relatedIdentifier) {
    return {...direct, resolvedFromSecondaryStore: false}
  }

  if (shouldResolveIdentifierFromSecondaryStore(selectedRole, relatedRole) && secondaryStoreData) {
    const crossResolved = resolveIdentifierPairFromSecondaryStore(
      secondaryStoreData.settings,
      secondaryStoreData.pairs,
      selectedPlatformId,
      relatedPlatformId,
      allowRootFallback
    )

    if (crossResolved.selectedIdentifier && crossResolved.relatedIdentifier) {
      return crossResolved
    }
  }

  return {...direct, resolvedFromSecondaryStore: false}
}

export const getIdentifierFieldKeysForRolePair = getIdentifierFieldKeys
